import { BookOpen, Link, Mic, Network, Target, Waves } from "lucide-react";
import { MetricData, MetricTotalData } from "@/hooks/api/useMetricsApi";

import { StreakCardProps } from "@/components/pages/metrics/streak-card";

export const MetricsList = [
  {
    key: "coherence",
    label: "pages:dashboard.metrics.coherence",
    description: "pages:dashboard.metricsDescription.coherence",
    icon: Network,
  },
  {
    key: "cohesion",
    label: "pages:dashboard.metrics.cohesion",
    description: "pages:dashboard.metricsDescription.cohesion",
    icon: Link,
  },
  {
    key: "contundence",
    label: "pages:dashboard.metrics.contundence",
    description: "pages:dashboard.metricsDescription.contundence",
    icon: Target,
  },
  {
    key: "lexicalResource",
    label: "pages:dashboard.metrics.lexicalResource",
    description: "pages:dashboard.metricsDescription.lexicalResource",
    icon: BookOpen,
  },
  {
    key: "pronunciation",
    label: "pages:dashboard.metrics.pronunciation",
    description: "pages:dashboard.metricsDescription.pronunciation",
    icon: Mic,
  },
  {
    key: "fluency",
    label: "pages:dashboard.metrics.fluency",
    description: "pages:dashboard.metricsDescription.fluency",
    icon: Waves,
  },
];

export const getMetricIcon = (metricKey: string) => {
  const metric = MetricsList.find((m) => m.key === metricKey);
  return metric ? metric.icon : null;
};

export interface FilledInMetricData {
  date: string;
  coherence: number | null;
  cohesion: number | null;
  contundence: number | null;
  lexicalResource: number | null;
  pronunciation: number | null;
  fluency: number | null;
  duration: number | null;
}

// Function to create full date range with null values for missing dates
export const fillInMissingData = (data: MetricData[]): FilledInMetricData[] => {
  if (data.length < 2) return data;

  // Sort data by date
  const sortedData = data; // already sorted from backend

  const result: FilledInMetricData[] = [];
  const startDate = new Date(sortedData[0].date);
  const endDate = new Date(sortedData[sortedData.length - 1].date);

  // Generate all dates between start and end
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const prevDate = new Date(currentDate);
    prevDate.setDate(currentDate.getDate() - 1);

    const dateStr = currentDate.toISOString().split("T")[0];

    // Check if we have data for this date
    const existingData = sortedData.find((item) => item.date === dateStr);

    if (existingData) {
      result.push(existingData);
    } else {
      // Add null value for missing dates to maintain spacing
      result.push({
        date: dateStr,
        coherence: null,
        cohesion: null,
        contundence: null,
        lexicalResource: null,
        pronunciation: null,
        fluency: null,
        duration: null,
      });
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return result;
};

export const roundToDecimalsFixed = (num: number, decimals: number): number => {
  return parseFloat(num.toFixed(decimals));
};

export const periodNormalizedTotalData = (
  data: FilledInMetricData[]
): MetricTotalData[] => {
  if (data.length === 0)
    return data.map(() => ({
      date: "",
      total: 0,
    }));

  return data.map((item) => {
    if (
      !item.coherence ||
      !item.cohesion ||
      !item.contundence ||
      !item.lexicalResource ||
      !item.pronunciation ||
      !item.fluency
    ) {
      return {
        date: item.date,
        total: null,
      };
    }

    const total =
      (item.coherence +
        item.cohesion +
        item.contundence +
        item.lexicalResource +
        item.pronunciation +
        item.fluency) /
      6; // Normalize to 100
    return {
      date: item.date,
      total: roundToDecimalsFixed(total, 1),
    };
  });
};

export const periodAverageData = (data: MetricData[]): MetricData => {
  if (data.length === 0)
    return {
      date: "",
      coherence: 0,
      cohesion: 0,
      contundence: 0,
      lexicalResource: 0,
      pronunciation: 0,
      fluency: 0,
      duration: 0,
    };

  const total = data.reduce(
    (acc, item) => ({
      coherence: acc.coherence + item.coherence,
      cohesion: acc.cohesion + item.cohesion,
      contundence: acc.contundence + item.contundence,
      lexicalResource: acc.lexicalResource + item.lexicalResource,
      pronunciation: acc.pronunciation + item.pronunciation,
      fluency: acc.fluency + item.fluency,
      duration: acc.duration + item.duration,
    }),
    {
      coherence: 0,
      cohesion: 0,
      contundence: 0,
      lexicalResource: 0,
      pronunciation: 0,
      fluency: 0,
      duration: 0,
    }
  );

  const count = data.length; // Use 1/6 for total to average across all metrics (normalized to 100)

  return {
    date: "Average",
    coherence: roundToDecimalsFixed(total.coherence / count, 1),
    cohesion: roundToDecimalsFixed(total.cohesion / count, 1),
    contundence: roundToDecimalsFixed(total.contundence / count, 1),
    lexicalResource: roundToDecimalsFixed(total.lexicalResource / count, 1),
    pronunciation: roundToDecimalsFixed(total.pronunciation / count, 1),
    fluency: roundToDecimalsFixed(total.fluency / count, 1),
    duration: roundToDecimalsFixed(total.duration / count, 1),
  };
};

export const computeStreak = (data: FilledInMetricData[]): StreakCardProps => {
  const REQUIRED_TIME_FOR_STREAK = 1; // Example: 1 minute required for streak

  // Data is already sorted by date ascending from API response
  const currentDate = new Date();
  const dateStr = currentDate.toISOString().split("T")[0];
  const lastActiveDate = data[data.length - 1]?.date;

  const todayTimeSpentMillis = data.find(
    (item) => item.date === dateStr
  )?.duration;

  const todayTimeSpentMins = todayTimeSpentMillis
    ? roundToDecimalsFixed(todayTimeSpentMillis / (60 * 1000), 2) // Convert milliseconds to minutes
    : 0;

  // Find the index of the target date
  const targetIndex = data.findIndex((item) => item.date === dateStr);

  if (targetIndex === -1) {
    return {
      currentStreak: 0,
      todayTimeSpent: todayTimeSpentMins,
      requiredTimeForStreak: REQUIRED_TIME_FOR_STREAK,
      lastActiveDate: lastActiveDate || "",
    };
  }

  let streak = 0;

  // Start from target date and go backwards, counting consecutive days with enough duration
  for (let i = targetIndex; i >= 0; i--) {
    const dataPoint = data[i];
    if (
      dataPoint &&
      dataPoint.duration &&
      roundToDecimalsFixed(dataPoint.duration / (60 * 1000), 2) >=
        REQUIRED_TIME_FOR_STREAK
    ) {
      streak++;
    } else {
      break; // Break the streak
    }
  }

  return {
    currentStreak: streak,
    todayTimeSpent: todayTimeSpentMins,
    requiredTimeForStreak: REQUIRED_TIME_FOR_STREAK,
    lastActiveDate: lastActiveDate,
  };
};
